import { Clock3, Factory, PauseCircle, PlayCircle, X } from "lucide-react";
import { sumKnownQuantities } from "../../../../shared/production/quantity";
import { amount } from "../operator/quantity-format";
import { LiveRunTimer } from "../operator/OperatorRunStatus";
import { hasVisibleWorkOrder, productionIdentityLabel } from "../work-order-display";
import { planDate, today } from "../production-time";
import { supervisorSummaryLine } from "./supervisor-board-model";
import { rate } from "./supervisor-format";
import type { SupervisorBoardWorkspace } from "./useSupervisorBoardWorkspace";

export function SupervisorSummaryDrawer({ workspace }: { workspace: SupervisorBoardWorkspace }) {
  const { reference, activeShiftCode, shiftRuns, shiftReport, lineFilter, setLineFilter, toggleLineFilter, setSummaryOpen } = workspace;
  const { shiftStoppedCount, shiftActiveCount, shiftReadyCount, summaryUnit, summaryProduction, summaryPlanned, summaryRemaining, summaryAttainment, summaryScrap, summaryDowntime, pendingCodingCount, filteredShiftReport } = workspace.model;
  return (
    <div className="production-summary-backdrop">
      <aside
        className="production-summary-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Vardiya kontrolü"
      >
        <header>
          <div>
            <small>CANLI İZLEME VE GERÇEKLEŞME</small>
            <h2>Vardiya kontrolü</h2>
            <span>
              {planDate(today())} · {activeShiftCode} ·{" "}
              {shiftReport.length} hat · 15 sn’de eşzamanlanır
            </span>
          </div>
          <div
            className="production-control-statuses"
            aria-label="Canlı durum özeti"
          >
            <button
              type="button"
              className="is-all"
              aria-pressed={lineFilter === "all"}
              onClick={() => setLineFilter("all")}
            >
              <Factory size={17} />
              <b>{shiftReport.length}</b> tümü
            </button>
            <button
              type="button"
              className="is-active"
              aria-pressed={lineFilter === "active"}
              onClick={() => toggleLineFilter("active")}
            >
              <PlayCircle size={17} />
              <b>{shiftActiveCount}</b> üretimde
            </button>
            <button
              type="button"
              className="is-ready"
              aria-pressed={lineFilter === "ready"}
              onClick={() => toggleLineFilter("ready")}
            >
              <Clock3 size={17} />
              <b>{shiftReadyCount}</b> hazır
            </button>
            <button
              type="button"
              className="is-stopped"
              aria-pressed={lineFilter === "stopped"}
              onClick={() => toggleLineFilter("stopped")}
            >
              <PauseCircle size={17} />
              <b>{shiftStoppedCount}</b> duruşta
            </button>
          </div>
          <button
            type="button"
            aria-label="Vardiya özetini kapat"
            onClick={() => setSummaryOpen(false)}
          >
            <X size={22} />
          </button>
        </header>
        <section className="production-summary-target">
          <div>
            <small>VARDİYA HEDEFİ</small>
            <strong>
              {summaryPlanned ? amount(summaryPlanned, summaryUnit) : "—"}
            </strong>
          </div>
          <div className="production-summary-target-progress">
            <span>
              <b>Gerçekleşme</b>
              <strong>%{(summaryAttainment == null ? "—" : Math.round(summaryAttainment))}</strong>
            </span>
            <div>
              <i style={{ width: `${summaryAttainment ?? 0}%` }} />
            </div>
            <small>
              {summaryPlanned
                ? `${amount(summaryProduction, summaryUnit)} üretildi · ${amount(summaryRemaining, summaryUnit)} kaldı`
                : "Bu vardiya için miktar hedefi bulunmuyor"}
            </small>
          </div>
        </section>
        <section className="production-summary-kpis">
          <span>
            <small>Gerçekleşen üretim</small>
            <strong>{amount(summaryProduction, summaryUnit)}</strong>
            <em>
              {summaryPlanned
                ? `Hedefin %${(summaryAttainment == null ? "—" : Math.round(summaryAttainment))}’i`
                : "Hedef tanımsız"}
            </em>
          </span>
          <span>
            <small>Toplam ıskarta</small>
            <strong>{amount(summaryScrap, summaryUnit)}</strong>
            <em>
              {summaryProduction != null && summaryProduction > 0 && summaryScrap != null
                ? `Üretimin %${((summaryScrap / summaryProduction) * 100).toFixed(1)}’i`
                : "Oran hesaplanamadı"}
            </em>
          </span>
          <span>
            <small>Toplam duruş</small>
            <strong>{summaryDowntime} dk</strong>
            <em>
              {summaryDowntime
                ? "Vardiya kaybı kaydedildi"
                : "Duruş kaydı yok"}
            </em>
          </span>
          <span className={pendingCodingCount ? "is-warning" : ""}>
            <small>Veri girişi</small>
            <strong>
              {pendingCodingCount
                ? `${pendingCodingCount} bekleyen`
                : "Tamam"}
            </strong>
            <em>
              {pendingCodingCount
                ? "Iskarta kodlaması gerekli"
                : "Bekleyen işlem yok"}
            </em>
          </span>
        </section>
        <div className="production-summary-lines">
          {filteredShiftReport.map((row) => {
            const { currentRun, lineStatusText, lineState, planned, progress, linePending, lineRate } = supervisorSummaryLine(row, shiftRuns, reference, Date.now());
            return (
              <article
                className={`is-line-${lineState}`}
                key={row.lineId}
              >
                <header>
                  <div>
                    <strong>{row.lineName}</strong>
                    <small
                      className={`production-line-status is-${lineState}`}
                    >
                      {lineStatusText}
                    </small>
                  </div>
                  <b>%{Math.round(progress)}</b>
                </header>
                <div className="production-summary-progress">
                  <span style={{ width: `${progress}%` }} />
                </div>
                {currentRun ? (
                  <section className="production-summary-current">
                    <span>
                      <small>
                        {currentRun.downtimes.some(
                          (item) => !item.endedAt,
                        )
                          ? "DURUŞTAKİ İŞ"
                          : currentRun.status === "ACTIVE"
                            ? "AKTİF İŞ"
                            : currentRun.status === "READY"
                              ? "SIRADAKİ İŞ"
                              : "SON TAMAMLANAN İŞ"}
                      </small>
                      <strong>{productionIdentityLabel(currentRun)}</strong>
                      {hasVisibleWorkOrder(currentRun) ? <em>{currentRun.productName}</em> : <em>Plandan üretim</em>}
                    </span>
                    <span>
                      <small>OPERATÖR</small>
                      <strong>
                        {currentRun.assignedOperatorName ??
                          currentRun.startedByName ??
                          "Atanmadı"}
                      </strong>
                    </span>
                    <LiveRunTimer run={currentRun} />
                  </section>
                ) : null}
                <section className="production-line-rate">
                  <span>
                    <small>GERÇEK HIZ</small>
                    <strong>{rate(lineRate.actualRate, lineRate.unit)}</strong>
                  </span>
                  <span>
                    <small>PLANLANAN</small>
                    <strong>{rate(lineRate.plannedRate, lineRate.unit)}</strong>
                  </span>
                  {lineRate.performance == null ? null : (
                    <b className={lineRate.performance < 85 ? "is-behind" : "is-on-target"}>
                      %{Math.round(lineRate.performance)} performans
                    </b>
                  )}
                </section>
                <dl>
                  <div>
                    <dt>Plan</dt>
                    <dd>{planned ? amount(planned) : "—"}</dd>
                  </div>
                  <div>
                    <dt>Üretim</dt>
                    <dd>{amount(row.production, row.unit)}</dd>
                  </div>
                  <div>
                    <dt>Kalan</dt>
                    <dd>
                      {planned != null && row.production != null
                        ? amount(Math.max(0, planned - row.production), row.unit)
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Iskarta</dt>
                    <dd>{amount(sumKnownQuantities([row.waste, row.scrap]), row.unit)}</dd>
                    {linePending ? (
                      <small>
                        {linePending} kayıt veri girişi bekliyor
                      </small>
                    ) : null}
                  </div>
                  <div>
                    <dt>Duruş</dt>
                    <dd>{row.downtimeMinutes} dk</dd>
                  </div>
                </dl>
              </article>
            );
          })}
          {!filteredShiftReport.length ? (
            <p className="production-summary-filter-empty">
              Bu durumda hat bulunmuyor.
            </p>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
